/**
 * AuthCallbackScreen — handles the OAuth Authorization Code callback at
 * /auth/callback?code=<code> (or ?error=<error>).
 *
 * Mounted by App.tsx when `window.location.pathname === '/auth/callback'`.
 * The component is responsible for:
 *   1. Parsing the URL search params
 *   2. Handling error redirects from Google (e.g. access_denied)
 *   3. Reading the PKCE verifier from sessionStorage
 *   4. POSTing (code, verifier) to /api/auth/exchange via postExchange
 *   5. On success: clearing the verifier, calling onAuthenticated
 *   6. On failure: calling onError with a classified reason
 *
 * While the exchange is in flight, renders a centered loading spinner.
 */

import { useEffect, useRef } from 'react';
import {
  postExchange,
  BadRequest,
  UpstreamUnavailable,
} from '@/lib/auth/serverRefresh';
import { readVerifier, clearVerifier } from '@/lib/auth/oauthRedirect';

export type CallbackErrorReason = 'access_denied' | 'verifier_missing' | 'exchange_failed';

export interface AuthCallbackScreenProps {
  /** Called with the access token + email returned by /api/auth/exchange. */
  onAuthenticated: (token: string, email: string | null) => void;
  /** Called when the callback cannot complete sign-in. */
  onError: (reason: CallbackErrorReason) => void;
}

export default function AuthCallbackScreen({
  onAuthenticated,
  onError,
}: AuthCallbackScreenProps): JSX.Element {
  // Use a ref-based run-once guard so React StrictMode double-invoke
  // doesn't fire two exchange requests.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (error) {
      clearVerifier();
      onError(error === 'access_denied' ? 'access_denied' : 'exchange_failed');
      return;
    }

    if (!code) {
      // No code and no error — malformed callback.
      clearVerifier();
      onError('exchange_failed');
      return;
    }

    const verifier = readVerifier();
    if (!verifier) {
      onError('verifier_missing');
      return;
    }

    void (async () => {
      try {
        const resp = await postExchange({ code, codeVerifier: verifier });
        clearVerifier();
        onAuthenticated(resp.access_token, resp.email ?? null);
      } catch (err) {
        clearVerifier();
        if (err instanceof BadRequest || err instanceof UpstreamUnavailable) {
          onError('exchange_failed');
        } else {
          // SessionExpired (app_check_failed) or anything else unexpected
          onError('exchange_failed');
        }
      }
    })();
  }, [onAuthenticated, onError]);

  return (
    <main
      aria-label="正在完成登入…"
      style={{
        minHeight: '100dvh',
        background: 'var(--bg-app)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
      }}
    >
      <div
        role="status"
        aria-label="載入中"
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '3px solid var(--border-1)',
          borderTopColor: 'var(--accent)',
          animation: 'mochia-spin 0.7s linear infinite',
        }}
      />
      <p
        style={{
          margin: 0,
          font: '400 14px var(--font-cjk)',
          color: 'var(--fg-3)',
        }}
      >
        正在完成登入，請稍候…
      </p>
      <style>{`@keyframes mochia-spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}
